"use client"

import { ArrayPath, Controller, FieldPath, FieldValues, useFieldArray, useFormContext } from "react-hook-form"
import { TButton } from "../../ui/button/TButton"
import { TIconButton } from "../../ui/button/TIconButton"
import { TIcon } from "../../ui/display/TIcon"
import { TText } from "../../ui/display/TText"
import { TNumberInput } from "../../ui/input/TNumberInput"
import { TTextInput } from "../../ui/input/TTextInput"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { TSlider } from "../../ui/slider/TSlider"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"

interface LorasFieldProps {
	field: ParsedField
}

export function LorasField({ field: parsedField }: LorasFieldProps) {
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	const { fields, append, remove } = useFieldArray({
		control,
		name: name as ArrayPath<FieldValues>
	})

	const minItems = (schema.minItems as number) || 1
	const maxItems = (schema.maxItems as number) || 3
	const defaultScale = 40

	function handleAddLora() {
		if (fields.length < maxItems) {
			append({ path: "", scale: defaultScale } as never)
		}
	}

	function handleRemoveLora(index: number) {
		if (fields.length > minItems) {
			remove(index)
		}
	}

	const canAddMore = fields.length < maxItems
	const canRemove = fields.length > minItems

	return (
		<FormItem className="bg-g800/10 rounded-lg p-3 flex flex-col gap-2 group">
			<div className="flex items-center justify-between w-full">
				<DynamicFormLabel name={name} title={title} required={required} description={description} />
				<TButton color="tertiary" size="small" onClick={handleAddLora} disabled={!canAddMore}>
					<TIcon name="add" size={16} />
					Add
				</TButton>
			</div>

			<div className="flex flex-col gap-2 w-full">
				{fields.map((field, index) => (
					<div key={field.id} className="relative bg-black/10 rounded-lg p-4 flex flex-col gap-3">
						<div className="flex flex-col gap-1">
							<TText variant="caption" color="tertiary" bold>
								Path
							</TText>
							<Controller
								name={`${name}.${index}.path` as FieldPath<FieldValues>}
								control={control}
								render={({ field: pathField }) => (
									<FormControl>
										<TTextInput size="small" value={pathField.value ?? ""} placeholder="Placeholder" onChange={pathField.onChange} hiddenClear />
									</FormControl>
								)}
							/>
						</div>

						<div className="flex flex-col gap-1">
							<TText variant="caption" color="tertiary" bold>
								Scale
							</TText>
							<div className="flex items-center gap-3">
								<Controller
									name={`${name}.${index}.scale` as FieldPath<FieldValues>}
									control={control}
									render={({ field: scaleField }) => {
										const currentValue = scaleField.value !== undefined && scaleField.value !== null ? scaleField.value : defaultScale
										return (
											<>
												<FormControl>
													<TSlider
														name={`scale-${index}`}
														value={[currentValue]}
														onChange={(value) => scaleField.onChange(value[0])}
														min={0}
														max={100}
														step={1}
														className="flex-1"
													/>
												</FormControl>
												<FormControl>
													<TNumberInput
														name={`scale-input-${index}`}
														value={currentValue}
														onChange={(value) => scaleField.onChange(value)}
														min={0}
														max={100}
														step={1}
														integerOnly
														size="small"
														className="w-[60px]! shrink-0 [&_input]:min-w-0!"
													/>
												</FormControl>
											</>
										)
									}}
								/>
							</div>
						</div>

						{canRemove && (
							<div className="absolute top-2 right-2">
								<TIconButton name="close" size="xsmall" color="ghost" onClick={() => handleRemoveLora(index)} />
							</div>
						)}
					</div>
				))}
			</div>

			<FormMessage />
		</FormItem>
	)
}
